import styles from './PieConfig.module.scss'
import { useCallback, useEffect, useState } from 'react'
import { propExistsOnObject } from '../../utils'
import { ScoopSlider, ScoopSelector, ScoopText, ScoopSwitch } from '../../../../../../ui'
import { PIE_DEFAULT_VALUES, PIE_ORIENTATIONS } from '../../styleConsts'
import { debounce, cloneDeep } from 'lodash'

export const PieConfig = ({ config, setConfig, pieDonut, chartPreferences }) => {
    const [innerRadius, setInnerRadius] = useState(PIE_DEFAULT_VALUES.radius[0])
    const [outerRadius, setOuterRadius] = useState(PIE_DEFAULT_VALUES.radius[1])
    const [categoryGap, setCategoryGap] = useState(PIE_DEFAULT_VALUES.padAngle)
    const [borderRadius, setBorderRadius] = useState(PIE_DEFAULT_VALUES.itemStyle.borderRadius)

    useEffect(() => {
        setInnerRadius(getPieRadiusValue('inner'))
        setOuterRadius(getPieRadiusValue('outer'))
        setCategoryGap(getPiePropValue('padAngle'))
        setBorderRadius(getPieBorderRadiusValue())
    }, [config])

    const borderRadiusDebouncedFunc = useCallback(
        debounce((prop, value) => {
            const newConfig = cloneDeep(config)
            newConfig.styleOverrides[pieDonut].itemStyle.borderRadius = value
            setConfig(newConfig)
        }, 200),
        [config]
    )

    const radiusDebouncedFunc = useCallback(
        debounce((prop, value) => {
            const newConfig = cloneDeep(config)
            if (!newConfig.styleOverrides?.[pieDonut]?.radius) {
                newConfig.styleOverrides[pieDonut] = {
                    ...newConfig.styleOverrides[pieDonut],
                    radius: [PIE_DEFAULT_VALUES.radius[0], PIE_DEFAULT_VALUES.radius[1]],
                }
            }
            if (prop === 'outer') newConfig.styleOverrides[pieDonut].radius[1] = value
            else newConfig.styleOverrides[pieDonut].radius[0] = value
            setConfig(newConfig)
        }, 200),
        [config]
    )

    const pieDebouncedFunc = useCallback(
        debounce((prop, value) => {
            const newConfig = cloneDeep(config)
            newConfig.styleOverrides[pieDonut][prop] = value
            setConfig(newConfig)
        }, 200),
        [config]
    )

    const labelDebouncedFunc = useCallback(
        debounce((prop, value) => {
            const newConfig = cloneDeep(config)
            newConfig.styleOverrides[pieDonut].label[prop] = value
            setConfig(newConfig)
        }, 200),
        [config]
    )

    const getPiePropValue = (prop) => {
        if (propExistsOnObject(config.styleOverrides[pieDonut], prop))
            return config.styleOverrides[pieDonut][prop]
        if (propExistsOnObject(chartPreferences[pieDonut], prop))
            return chartPreferences[pieDonut][prop]
        return PIE_DEFAULT_VALUES[prop]
    }

    const setPiePropValue = (prop, value) => {
        const newConfig = cloneDeep(config)
        newConfig.styleOverrides[pieDonut][prop] = value
        setConfig(newConfig)
    }

    const setDebouncedPiePropValue = (prop, value, setter) => {
        pieDebouncedFunc(prop, value)
        setter(value)
    }

    const getPieBorderRadiusValue = () => {
        if (propExistsOnObject(config.styleOverrides[pieDonut].itemStyle, 'borderRadius'))
            return config.styleOverrides[pieDonut].itemStyle.borderRadius
        if (propExistsOnObject(chartPreferences[pieDonut]?.itemStyle, 'borderRadius'))
            return chartPreferences[pieDonut].itemStyle.borderRadius
        return PIE_DEFAULT_VALUES.itemStyle.borderRadius
    }

    const setPieBorderRadius = (prop, value, setter) => {
        borderRadiusDebouncedFunc(prop, value)
        setter(value)
    }

    const getPieRadiusValue = (prop) => {
        if (prop === 'outer') {
            if (propExistsOnObject(config.styleOverrides[pieDonut], 'radius'))
                return config.styleOverrides[pieDonut].radius[1]
            if (propExistsOnObject(chartPreferences[pieDonut], 'radius'))
                return chartPreferences[pieDonut].radius[1]
            return PIE_DEFAULT_VALUES.radius[1]
        } else {
            if (propExistsOnObject(config.styleOverrides[pieDonut], 'radius'))
                return config.styleOverrides[pieDonut].radius[0]
            if (propExistsOnObject(chartPreferences[pieDonut], 'radius'))
                return chartPreferences[pieDonut].radius[0]
            return PIE_DEFAULT_VALUES.radius[0]
        }
    }

    const setPieRadiusValue = (prop, value, setter) => {
        radiusDebouncedFunc(prop, value)
        setter(value)
    }

    const getPieEmphasisValue = (prop) => {
        if (propExistsOnObject(config.styleOverrides[pieDonut].emphasis, prop))
            return config.styleOverrides[pieDonut].emphasis[prop]
        if (propExistsOnObject(chartPreferences[pieDonut]?.emphasis, prop))
            return chartPreferences[pieDonut].emphasis[prop]
        return PIE_DEFAULT_VALUES.emphasis[prop]
    }

    const setPieEmphasisValue = (prop, value) => {
        const newConfig = cloneDeep(config)
        newConfig.styleOverrides[pieDonut].emphasis[prop] = value
        setConfig(newConfig)
    }

    const getPieOrientationValue = () => {
        const startAngle = getPiePropValue('startAngle')
        const endAngle = getPiePropValue('endAngle')
        if (startAngle === 0 && endAngle === 360) {
            return 'full'
        } else if (startAngle === 90 && endAngle === 270) {
            return 'right'
        } else if (startAngle === 360 && endAngle === 180) {
            return 'bottom'
        } else if (startAngle === 180 && endAngle === 360) {
            return 'top'
        } else {
            return 'left'
        }
    }

    const setPieOrientationValue = (value) => {
        const newConfig = cloneDeep(config)
        switch (value) {
            case 'top':
                newConfig.styleOverrides[pieDonut].startAngle = 180
                newConfig.styleOverrides[pieDonut].endAngle = 360
                break
            case 'full':
                newConfig.styleOverrides[pieDonut].startAngle = 0
                newConfig.styleOverrides[pieDonut].endAngle = 360
                break
            case 'right':
                newConfig.styleOverrides[pieDonut].startAngle = 90
                newConfig.styleOverrides[pieDonut].endAngle = 270
                break
            case 'bottom':
                newConfig.styleOverrides[pieDonut].startAngle = 360
                newConfig.styleOverrides[pieDonut].endAngle = 180
                break
            case 'left':
                newConfig.styleOverrides[pieDonut].startAngle = 270
                newConfig.styleOverrides[pieDonut].endAngle = 90
                break
        }
        setConfig(newConfig)
    }

    const nightingale = getPiePropValue('roseType')
    const scale = getPieEmphasisValue('scale')

    // Convert PIE_ORIENTATIONS to options format for ScoopSelector
    const orientationOptions = PIE_ORIENTATIONS.map((orientation) => ({
        value: orientation.value,
        label: orientation.label,
    }))

    return (
        <div className={styles.container}>
            <div className={styles.section}>
                <ScoopSlider
                    label={`Inner radius: ${innerRadius}`}
                    value={innerRadius?.split('%')[0]}
                    onChange={(v) => setPieRadiusValue('inner', v + '%', setInnerRadius)}
                    min={0}
                    max={100}
                    step={1}
                />
                <ScoopSlider
                    label={`Outer radius: ${outerRadius}`}
                    value={outerRadius?.split('%')[0]}
                    onChange={(v) => setPieRadiusValue('outer', v + '%', setOuterRadius)}
                    min={0}
                    max={100}
                    step={1}
                />
                <ScoopSlider
                    label={`Category gap: ${categoryGap}`}
                    value={categoryGap}
                    onChange={(v) => setDebouncedPiePropValue('padAngle', v, setCategoryGap)}
                    min={0}
                    max={10}
                    step={1}
                />
            </div>
            <div className={styles.section}>
                <div className={styles.switchRow}>
                    <ScoopSwitch
                        checked={!!nightingale}
                        onChange={() =>
                            !!nightingale
                                ? setPiePropValue('roseType', '')
                                : setPiePropValue('roseType', 'area')
                        }
                    />
                    <ScoopText variant="label">Show Nightingale</ScoopText>
                </div>
            </div>
            <div className={styles.section}>
                <div className={styles.switchRow}>
                    <ScoopSwitch
                        checked={scale}
                        onChange={() => setPieEmphasisValue('scale', !scale)}
                    />
                    <ScoopText variant="label">Scale on emphasis</ScoopText>
                </div>
            </div>
            <ScoopSelector
                label="Pie orientation"
                value={getPieOrientationValue()}
                onChange={(v) => setPieOrientationValue(v)}
                options={orientationOptions}
                size="md"
                removeMargin
            />
            <ScoopSlider
                label={`Border radius: ${borderRadius}px`}
                value={borderRadius}
                onChange={(v) => setPieBorderRadius('IS', v, setBorderRadius)}
                min={0}
                max={50}
            />
            <hr className={styles.divider} />
        </div>
    )
}
